import { Editor, createShapeId } from '@tldraw/tldraw'
import { FrameShape, FrameShapeProps } from './Types'
import { sortBy } from 'lodash'
import { CAPTION_GAP, FRAMES_GAP } from './constants'
import { setCameraBoundaries } from 'components/VisualStudioPlayground/VisualEditor/Bootstrap/DefaultToolsOverrides'
import { getCurrentFrame } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { zoomToInitialBounds } from 'components/Design/utils/getInitialSceneBounds'

export function getOrderedFrames(editor: Editor): Array<FrameShape> {
  const allFrames = editor.getCurrentPageShapes().filter((shape): shape is FrameShape => shape.type === 'frame')
  return sortBy(allFrames, (shape) => shape.props.position)
}

export function getByPosition(editor: Editor, position: number) {
  const frames = getOrderedFrames(editor)

  return frames.find((frame) => frame.props.position === position)
}

export function moveFrame(
  arr: Array<FrameShape>,
  currentPos: number,
  newPos: number,
  fileSupportsCaption: boolean,
): Array<FrameShape> {
  const currentIndex = currentPos - 1
  const newIndex = newPos - 1
  if (currentIndex < 0 || currentIndex >= arr.length || newIndex < 0 || newIndex >= arr.length) {
    return arr
  }

  const elementToMove = arr[currentIndex]
  const updatedArray = [...arr]

  // Remove the element from its current position
  updatedArray.splice(currentIndex, 1)

  // Insert the element at the new position
  updatedArray.splice(newIndex, 0, elementToMove)

  return repositionFrames(updatedArray, fileSupportsCaption)
}

export function deleteFrame(arr: Array<FrameShape>, pos: number, fileSupportsCaption: boolean): Array<FrameShape> {
  const index = pos - 1
  if (index < 0 || index >= arr.length) {
    return arr
  }

  const updatedArray = [...arr]
  // Remove the element from its current position
  updatedArray.splice(index, 1)
  return repositionFrames(updatedArray, fileSupportsCaption)
}

export function insertFrame(
  arr: Array<FrameShape>,
  frame: FrameShape,
  pos: number,
  fileSupportsCaption: boolean,
): Array<FrameShape> {
  const index = pos - 1
  if (index < 0 || index > arr.length) {
    return arr
  }

  const updatedArray = [...arr]
  // Insert the element at the given position
  updatedArray.splice(index, 0, frame)
  return repositionFrames(updatedArray, fileSupportsCaption)
}

export function repositionFrames(frames: Array<FrameShape>, fileSupportsCaption: boolean): Array<FrameShape> {
  let y = 0

  return frames.map((frame, index) => {
    const position = index + 1
    const repositionedFrame = {
      ...frame,
      y,
      props: {
        ...frame.props,
        position,
      },
    }
    y = y + frame.props.h + FRAMES_GAP
    if (position === 1 && fileSupportsCaption) y += CAPTION_GAP
    return repositionedFrame
  })
}

export function addFrame(editor: Editor, fileSupportsCaption: boolean, props: Partial<FrameShapeProps> = {}): void {
  const orderedFrames = getOrderedFrames(editor)
  editor.batch(() => {
    const newId = createShapeId()
    editor.createShape({
      id: newId,
      type: 'frame',
      props: {
        fill: {
          type: 'solidColor',
          opacity: 1,
          color: '#FFFFFF',
        },
        position: orderedFrames.length + 1,
        h: orderedFrames[0].props.h,
        w: orderedFrames[0].props.w,
        ...props,
      },
    })
    const newFrame = editor.getShape<FrameShape>(newId)
    if (!newFrame) return
    const reorderedFrames = insertFrame(orderedFrames, newFrame, newFrame.props.position, fileSupportsCaption)
    editor.updateShapes(reorderedFrames)
  })
}

export const setCameraToCurrentFrame = (
  editor: Editor,
  {
    isMobile,
    animate,
    padding,
  }: {
    animate?: boolean
    isMobile: boolean
    padding?: { top: number; bottom: number }
  },
) => {
  const pad = padding ?? { top: 0, bottom: 0 }

  setCameraBoundaries(editor, pad, isMobile, (editor) => {
    if (isMobile) {
      editor.updateInstanceState({ canMoveCamera: true })
    }
    const frame = getCurrentFrame(editor)
    if (frame) {
      zoomToInitialBounds({ editor, frame, padding: pad, animate })
    }
    if (isMobile) {
      editor.updateInstanceState({ canMoveCamera: false })
    }
  })
}
